import React from 'react';

import TextField from '@material-ui/core/TextField';

import IProduct from 'models/product';
import AiFieldVerdict from '../AiFieldVerdict';
import {
  IPhraseSpec,
  getFillValue,
  getPhraseSpec,
  resolvePhrase,
  upsertFill,
} from './hazard-placeholders';

type FillsField = 'hazardStatementFills' | 'securityNoticeFills';

interface IPhraseFillsProps {
  readOnly?: boolean;
  state: IProduct;
  setState: (state: IProduct) => void;
  selectedCodes: readonly string[];
  fillsField: FillsField;
}

interface IPhraseFillCardProps {
  readOnly?: boolean;
  code: string;
  spec: IPhraseSpec;
  slotValues: string[];
  onSlotChange: (slotIndex: number, value: string) => void;
}

const cardStyle: React.CSSProperties = {
  marginBottom: 12,
  padding: 12,
  border: '1px solid #E0E0E0',
  borderRadius: 4,
  background: '#FFFFFF',
};

const codeHeaderStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
  marginBottom: 8,
};

const previewStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: 13,
  color: '#555',
};

const PhraseFillCard = ({ readOnly, code, spec, slotValues, onSlotChange }: IPhraseFillCardProps) => {
  const preview = resolvePhrase(spec, slotValues);

  return (
    <div style={cardStyle}>
      <div style={codeHeaderStyle}>{code}</div>
      {spec.slots.map((slot, i) => {
        const value = slotValues[i];
        const showWarning = !slot.optional && !value.trim();
        return (
          <TextField
            disabled={readOnly}
            key={i}
            fullWidth
            variant="outlined"
            size="small"
            margin="dense"
            label={`${slot.hint}${slot.optional ? '' : ' *'}`}
            placeholder={slot.placeholder}
            value={value}
            onChange={event => onSlotChange(i, event.target.value)}
            error={showWarning}
            helperText={showWarning ? 'Krävs för en komplett export.' : undefined}
          />
        );
      })}
      <div style={previewStyle}>
        Förhandsvisning: <span style={{ color: '#000' }}>{`${code} ${preview}`}</span>
      </div>
    </div>
  );
};

const PhraseFills = ({ readOnly, state, setState, selectedCodes, fillsField }: IPhraseFillsProps) => {
  const codesWithSpec = selectedCodes
    .map(code => ({ code, spec: getPhraseSpec(code) }))
    .filter((entry): entry is { code: string; spec: IPhraseSpec } => Boolean(entry.spec));

  if (codesWithSpec.length === 0) return null;

  const fills = state[fillsField];

  const handleSlotChange = (code: string) => (slotIndex: number, value: string) => {
    setState({ ...state, [fillsField]: upsertFill(fills, code, slotIndex, value) });
  };

  return (
    // FC-1950: scroll target of the "required text slots" error label.
    <div id={fillsField} style={{ marginTop: 8 }}>
      {codesWithSpec.map(({ code, spec }) => (
        <PhraseFillCard
          readOnly={readOnly}
          key={code}
          code={code}
          spec={spec}
          slotValues={spec.slots.map((_, i) => getFillValue(fills, code, i))}
          onSlotChange={handleSlotChange(code)}
        />
      ))}
      <AiFieldVerdict field={fillsField} />
    </div>
  );
};

export default PhraseFills;
